import DocumentPreviewRenderer from 'domain/shared/documentStorage/shared/DocumentTemplatePreviewSection/DocumentPreviewRenderer';
import { CommaListField, EnumField } from 'framework/ui/field';
import {
  COMPLIANCE_DOCUMENT_TYPE_VALUES,
  GRACE_PERIOD_DAYS,
  ComplianceDocumentFormType,
} from 'domain/shared/compliance';
import { Box, Typography } from '@mui/material';
import PositionEnumField from 'domain/shared/presale/field/PositionEnumField';
import { EMPLOYEE_CONTRACT_TYPES, EmployeeOfficeField } from 'domain/shared/employee';
import useDocumentBlob from 'domain/shared/documentStorage/shared/useDocumentBlob';
import { MESSAGE_MODAL_FORM_CONTEXT } from 'framework/form';
import { AssignmentsPreviewValue, PreviewField } from './ComplianceDocumentFormPreviewFields';

const ComplianceDocumentFormPreview = ({
  formData,
  documentPath,
}: {
  formData: ComplianceDocumentFormType;
  documentPath: string;
}) => {
  const {
    complianceName,
    complianceType,
    gracePeriodDays,
    isSignatureRequired,
    isAllEmployee,
    positionIds,
    offices,
    businessRegistrationCountries,
    relatedEmployeeContractTypes,
    assignments,
  } = formData || {};
  const { blobUrl, loading, hasError } = useDocumentBlob(documentPath);

  return (
    <Box display="flex" justifyContent="space-between">
      <Box width="45%">
        <Typography variant="h4" mb={0.5}>
          {complianceName}
        </Typography>

        <Typography variant="subtitle1" color="textSecondary" mb={2}>
          {COMPLIANCE_DOCUMENT_TYPE_VALUES[complianceType] || COMPLIANCE_DOCUMENT_TYPE_VALUES.policy}
        </Typography>

        {isSignatureRequired && gracePeriodDays && (
          <Box display="flex" mb={1}>
            <Typography>Grace period:&nbsp;</Typography>
            <EnumField options={GRACE_PERIOD_DAYS} value={String(gracePeriodDays)} />
          </Box>
        )}

        {isAllEmployee ? (
          <Box display="flex">
            <Typography>Target audience:&nbsp;</Typography>
            <Typography>To all workers</Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" mb={0.5}>
              <Typography>Target audience:&nbsp;</Typography>
            </Box>

            {Boolean(positionIds?.length) && (
              <PreviewField
                label="Positions"
                value={positionIds}
                isAll={false}
                renderValue={value => (
                  <CommaListField
                    value={value}
                    isNeedJoin={false}
                    mapValue={val => (
                      <Box>
                        <PositionEnumField value={val} />
                      </Box>
                    )}
                  />
                )}
              />
            )}

            {Boolean(offices?.length) && (
              <PreviewField
                label="Offices"
                value={offices}
                isAll={false}
                renderValue={value => (
                  <CommaListField
                    value={value}
                    isNeedJoin={false}
                    mapValue={val => (
                      <Box>
                        <EmployeeOfficeField value={Number(val)} />
                      </Box>
                    )}
                  />
                )}
              />
            )}

            {Boolean(relatedEmployeeContractTypes?.length) && (
              <PreviewField
                label="Contract types"
                value={relatedEmployeeContractTypes}
                isAll={false}
                renderValue={value => (
                  <CommaListField
                    value={value}
                    isNeedJoin={false}
                    mapValue={val => (
                      <Box>
                        <EnumField options={EMPLOYEE_CONTRACT_TYPES} value={val} />
                      </Box>
                    )}
                  />
                )}
              />
            )}

            {!!businessRegistrationCountries?.length && (
              <PreviewField
                label="Business registration country"
                value={businessRegistrationCountries}
                isAll={false}
                renderValue={value => (value || []).join(', ')}
              />
            )}

            {Boolean(assignments?.length) && (
              <PreviewField
                label="Assignments"
                value={assignments}
                isAll={false}
                renderValue={value => <AssignmentsPreviewValue assignments={value} />}
              />
            )}
          </>
        )}
      </Box>

      <Box width="55%">
        <DocumentPreviewRenderer
          documentPath={documentPath}
          blobUrl={blobUrl}
          loading={loading}
          hasError={hasError}
          defaultScale={0.5}
          messageContext={MESSAGE_MODAL_FORM_CONTEXT}
        />
      </Box>
    </Box>
  );
};

export default ComplianceDocumentFormPreview;
